import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Table, useToast, type Column } from 'grabbitbot-legacy-ui'
import { portalClient, tradingClient } from '../../api'
import type { DefaultConfig } from '../../api/types'
import type { CoinConfig, Strategy, StrategyCoin } from '../../api/tradingTypes'
import { CoinDetailsPopup } from './CoinDetailsPopup'
import { CoinSettingsPopup } from './CoinSettingsPopup'
import { MarginNotSupported } from './StrategyListPage'
import { isNotTrading, tradingModeLabel } from './coinConfigLabels'
import { readLastExchangeId } from './settingsData'
import { PRIMARY_USD_ASSET } from './tradingConstants'
import { failureText } from '../../api/errorMessages'

/** Owner-only visibility explanations — verbatim from the legacy sheet
 * (the only place these semantics are documented). */
const VISIBILITY: Record<number, { label: string; lines: string[] }> = {
  0: {
    label: 'Private',
    lines: ['Not listed on marketplace', 'No user can open datasheet with URL', 'No user can subscribe to it'],
  },
  1: {
    label: 'Closed',
    lines: [
      'Not listed on marketplace',
      'No user can open datasheet with URL, except previous subscribers',
      'No new user can subscribe to it',
      'Previous subscribers remain active',
    ],
  },
  2: {
    label: 'Hidden',
    lines: ['Not listed on marketplace', 'Users can open datasheet only via URL', 'Users, who knows the URL, can subscribe to it'],
  },
  3: { label: 'Public', lines: ['Listed on marketplace', 'Anyone can subscribe to it'] },
}

/** Legacy `V3ExchangeService.exchanges` label map (header uses the same). */
const EXCHANGE_LABELS: Record<string, string> = { binance: 'Binance', wnfexchange: 'WnF Exchange' }
function exchangeLabel(item: DefaultConfig): string {
  const base = (item.exchange && EXCHANGE_LABELS[item.exchange]) || item.exchange || item.exchangeId
  return `${base}${item.exchangeSubId ? ` (${item.name || item.exchangeSubId})` : ''}`
}

interface CoinRow extends StrategyCoin {
  subscription: CoinConfig | null
}

/**
 * Equivalent of legacy `V3StrategySheetSpotComponent` (route
 * `strategy/:tradingType/:id`): one strategy's info, and per exchange
 * account its coin pairs with per-pair Subscribe / Unsubscribe (body `{}`,
 * no confirmation — as legacy) plus the coin settings/details popups for
 * subscribed pairs.
 *
 * Not in this batch (B3 statistics): the per-coin profit chart, the "My total
 * profit" card and the resolution/days picker.
 */
export function StrategySheetPage() {
  const { id = '', tradingType = 'spot' } = useParams<{ id: string; tradingType: string }>()
  if (tradingType !== 'spot') return <MarginNotSupported title="Strategy" />
  return <SpotStrategySheet key={id} id={id} />
}

function SpotStrategySheet({ id }: { id: string }) {
  const { showToast } = useToast()
  const [strategy, setStrategy] = useState<Strategy | null>(null)
  const [strategyError, setStrategyError] = useState<string | null>(null)
  const [coins, setCoins] = useState<StrategyCoin[] | null>(null)
  const [exchanges, setExchanges] = useState<DefaultConfig[]>([])
  const [exchangeId, setExchangeId] = useState(() => readLastExchangeId())
  const [subscribed, setSubscribed] = useState<Record<string, CoinConfig>>({})
  const [subsLoading, setSubsLoading] = useState(false)
  const [subsToken, setSubsToken] = useState(0)
  const [changing, setChanging] = useState<string | null>(null)
  const [coinFilter, setCoinFilter] = useState('')
  const [selectedCoin, setSelectedCoin] = useState<CoinConfig | null>(null)
  const [detailsCoin, setDetailsCoin] = useState<CoinConfig | null>(null)
  const [pageToken, setPageToken] = useState(0)

  useEffect(() => {
    let cancelled = false
    tradingClient
      .viewStrategy(id)
      .then((result) => {
        if (cancelled) return
        setStrategy(result)
        setStrategyError(null)
        return tradingClient.searchStrategyCoins(id).then((list) => {
          if (!cancelled) setCoins(Array.isArray(list) ? list : [])
        })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const status = (err as { status?: number }).status
        setStrategyError(status === 410 ? 'This strategy does not exist.' : failureText(err, 'Could not load the strategy'))
      })
    portalClient
      .searchDefaultConfigs('spot')
      .then((list) => {
        if (cancelled) return
        const items = Array.isArray(list) ? list : []
        setExchanges(items)
        setExchangeId((current) => (items.some((e) => e.exchangeId === current) ? current : (items[0]?.exchangeId ?? '')))
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [id, pageToken])

  // Legacy `refreshSubscribedCoinList()`: the user's coin configs on the
  // chosen exchange, restricted to this strategy.
  useEffect(() => {
    setSubscribed({})
    if (!exchangeId) return
    let cancelled = false
    setSubsLoading(true)
    tradingClient
      .searchCoinConfigs(exchangeId, { strategyId: id })
      .then((list) => {
        if (cancelled) return
        const map: Record<string, CoinConfig> = {}
        for (const c of list) map[c.coin1 + c.coin2] = c
        setSubscribed(map)
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setSubsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [exchangeId, id, subsToken])

  const change = useCallback(
    async (coin: StrategyCoin, action: 'subscribe' | 'unsubscribe') => {
      if (!strategy || !exchangeId) return
      setChanging(coin.coin1 + coin.coin2)
      try {
        if (action === 'subscribe') {
          await tradingClient.subscribeCoinConfig(exchangeId, coin.coin1, coin.coin2, strategy.traderId, id, {})
        } else {
          await tradingClient.unsubscribeCoinConfig(exchangeId, coin.coin1, coin.coin2, {})
        }
        setSubsToken((n) => n + 1)
      } catch (err) {
        showToast(
          failureText(err, action === 'subscribe' ? 'Could not subscribe to the coin pair' : 'Could not unsubscribe from the coin pair'),
          'error',
        )
      } finally {
        setChanging(null)
      }
    },
    [exchangeId, id, showToast, strategy],
  )

  const rows: CoinRow[] = useMemo(
    () =>
      (coins ?? [])
        .filter((c) => coinFilter === '' || (c.coin1 + c.coin2).includes(coinFilter.toUpperCase()))
        .map((c) => ({ ...c, subscription: subscribed[c.coin1 + c.coin2] ?? null })),
    [coins, coinFilter, subscribed],
  )

  if (strategyError) {
    return (
      <div className="portal-card">
        <h1>Strategy</h1>
        <p role="alert">{strategyError}</p>
      </div>
    )
  }

  const owner = strategy?.userState === 1
  const title = strategy ? (owner ? strategy.name : strategy.realPublicName) : id

  const columns: Column<CoinRow>[] = [
    {
      key: 'coin',
      label: 'Coin',
      align: 'left',
      sortValue: (r) => r.coin1 + r.coin2,
      format: (_v, r) =>
        r.subscription ? (
          <span className="portal-inline-actions">
            <Link to={`/${exchangeId}/spot/coins/${r.coin1}/${r.coin2}`}>
              {r.coin1}
              {r.coin2}
            </Link>
            <button type="button" aria-label={`Details of ${r.coin1}${r.coin2}`} onClick={() => setDetailsCoin(r.subscription)}>
              ⓘ
            </button>
            <button type="button" aria-label={`Settings of ${r.coin1}${r.coin2}`} onClick={() => setSelectedCoin(r.subscription)}>
              ⚙
            </button>
          </span>
        ) : (
          `${r.coin1}${r.coin2}`
        ),
    },
    {
      key: 'bid',
      label: 'Bid',
      align: 'right',
      numeric: true,
      sortValue: (r) => Number(r.subscription?.effective?.bid ?? -1),
      format: (_v, r) =>
        r.subscription ? (
          <>
            {String(r.subscription.effective?.bid ?? '')} {r.subscription.coin2}
            {r.subscription.bidConstant && <span className="portal-muted-small">{r.subscription.bidConstant}</span>}
          </>
        ) : (
          <span className="portal-text-muted">{subsLoading ? '-' : 'available only for subscribed users'}</span>
        ),
    },
    {
      key: 'grid',
      label: 'Grid',
      align: 'right',
      numeric: true,
      sortValue: (r) => Number(r.subscription?.effective?.gridUp ?? -1),
      format: (_v, r) => {
        const s = r.subscription
        if (!s) return ''
        const up = s.effective?.gridUp
        const down = s.effective?.gridDown
        return (
          <>
            {String(up) === String(down) ? (
              <span title="UP and DOWN">{String(up ?? '')}% ⇅</span>
            ) : (
              <span>
                <span title="UP">{String(up ?? '')}% ↑</span> / <span title="DOWN">{String(down ?? '')}% ↓</span>
              </span>
            )}
            {s.gridUpConstant && s.gridUpConstant === s.gridDownConstant && <span className="portal-muted-small">{s.gridUpConstant}</span>}
            {s.gridUpConstant !== s.gridDownConstant && (
              <>
                {s.gridUpConstant && <span className="portal-muted-small">{s.gridUpConstant} ↑</span>}
                {s.gridDownConstant && <span className="portal-muted-small">{s.gridDownConstant} ↓</span>}
              </>
            )}
          </>
        )
      },
    },
    {
      key: 'tradingMode',
      label: 'Trading mode',
      align: 'left',
      sortValue: (r) => Number(r.subscription?.tradingMode ?? -1000),
      format: (_v, r) => (r.subscription ? tradingModeLabel(r.subscription) : ''),
    },
    {
      key: 'action',
      label: '',
      sortable: false,
      format: (_v, r) =>
        r.subscription ? (
          <button type="button" className="portal-button-danger" disabled={changing !== null} onClick={() => change(r, 'unsubscribe')}>
            {changing === r.coin1 + r.coin2 ? 'Unsubscribing…' : 'Unsubscribe'}
          </button>
        ) : (
          <button type="button" disabled={changing !== null || !exchangeId} onClick={() => change(r, 'subscribe')}>
            {changing === r.coin1 + r.coin2 ? 'Subscribing…' : 'Subscribe'}
          </button>
        ),
    },
  ]

  const visibility = strategy ? VISIBILITY[strategy.visibility] : undefined

  return (
    <div>
      <div className="portal-page-title">
        <h1>Strategy - {title}</h1>
      </div>
      {!strategy && <p>Loading…</p>}
      {strategy && (
        <div className="portal-grid-2" style={{ gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 3fr)' }}>
          <div>
            <div className="portal-card">
              <h2>
                Strategy
              </h2>
              {owner && <p className="portal-notice portal-notice--info">You are the owner of this strategy</p>}
              <h3>{title}</h3>
              <p>{strategy.description}</p>
            </div>
            <div className="portal-card">
              <h2>Info</h2>
              <dl className="portal-kv">
                {owner && visibility && (
                  <>
                    <dt>Visibility</dt>
                    <dd>
                      <strong>{visibility.label}</strong>
                      <ul>
                        {visibility.lines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </dd>
                  </>
                )}
                {Boolean(strategy.usdtNeeded) && (
                  <>
                    <dt>{PRIMARY_USD_ASSET} needed</dt>
                    <dd>{String(strategy.usdtNeeded)}</dd>
                  </>
                )}
                <dt>Realises loss</dt>
                <dd>{strategy.realisesLoss ? 'Yes ⚠' : 'No'}</dd>
                <dt>Profit share</dt>
                <dd>{strategy.profitShare === -1 ? strategy.defaultProfitShare : strategy.profitShare}%</dd>
                {(strategy.monthlyFee > 0 || (strategy.monthlyFee === -1 && Boolean(strategy.defaultMonthlyFee))) && (
                  <>
                    <dt>Monthly fee</dt>
                    <dd>${strategy.monthlyFee === -1 ? strategy.defaultMonthlyFee : strategy.monthlyFee}</dd>
                  </>
                )}
              </dl>
            </div>
            <div className="portal-card">
              <h2>Trader</h2>
              <h3>{strategy.traderId}</h3>
              <Link to={`/trader/${strategy.traderId}`}>details</Link>
            </div>
          </div>
          <div className="portal-card">
            <h2>Coins</h2>
            <div className="portal-tabs portal-tabs--sub" role="tablist" aria-label="Exchange">
              {exchanges.map((item) => (
                <button
                  key={item.exchangeId}
                  type="button"
                  role="tab"
                  aria-selected={item.exchangeId === exchangeId}
                  className={item.exchangeId === exchangeId ? 'portal-tab portal-tab--active' : 'portal-tab'}
                  onClick={() => setExchangeId(item.exchangeId)}
                >
                  {exchangeLabel(item)}
                </button>
              ))}
            </div>
            <input
              type="search"
              placeholder="search coins"
              aria-label="Search coins"
              value={coinFilter}
              onChange={(e) => setCoinFilter(e.target.value)}
            />
            {(!coins || subsLoading) && <p>Loading…</p>}
            {coins && (
              <div className="portal-table-scroll">
                <Table
                  columns={columns}
                  rows={rows}
                  rowKey={(r) => r.coin1 + r.coin2}
                  initialSort={null}
                  rowClassName={(r) => (r.subscription && isNotTrading(r.subscription) ? 'portal-row--not-trading' : undefined)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <CoinDetailsPopup coinConfig={detailsCoin} onClose={() => setDetailsCoin(null)} />
      <CoinSettingsPopup
        coinConfig={selectedCoin ? { ...selectedCoin, exchangeId: selectedCoin.exchangeId || exchangeId } : null}
        onClose={(refresh) => {
          setSelectedCoin(null)
          if (refresh) {
            setPageToken((n) => n + 1)
            setSubsToken((n) => n + 1)
          }
        }}
      />
    </div>
  )
}
